import type { MembershipStatus, MembershipPlan, IsoDate } from '@techsofit/api-types';
import { today, parseDate, daysBetween, dateToIso } from './format';

/**
 * Membership status — computed from the member's current expiry, never stored
 * as the source of truth (spec §7). Mirrors the backend's single function.
 */
export function deriveStatus(currentExpiry: IsoDate | null, expiryWarningDays: number): MembershipStatus {
  if (!currentExpiry) return 'NEVER_PAID';
  const days = daysBetween(today(), parseDate(currentExpiry));
  if (days < 0) return 'LAPSED';
  if (days <= expiryWarningDays) return 'EXPIRING';
  return 'ACTIVE';
}

/** Gym access = a valid membership (ACTIVE or EXPIRING). Lapsed / never-paid cannot self check-in. */
export function hasGymAccess(status: MembershipStatus | null | undefined): boolean {
  return status !== 'LAPSED' && status !== 'NEVER_PAID';
}

/** Maps the API status enum to the UI badge status. */
export function statusToBadge(s: MembershipStatus): 'active' | 'expiring' | 'lapsed' | 'never' {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'EXPIRING':
      return 'expiring';
    case 'LAPSED':
      return 'lapsed';
    case 'NEVER_PAID':
      return 'never';
  }
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Calendar-month add that clamps the day (Jan 31 + 1mo → Feb 28/29). Mirrors Java's plusMonths. */
function addMonths(d: Date, n: number): Date {
  const day = d.getDate();
  const r = new Date(d);
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(day, lastDay));
  return r;
}

function toIso(d: Date): IsoDate {
  return dateToIso(d);
}

/**
 * Compute the coverage period for a new payment (spec §7, the one documented
 * place this rule lives). Stacking renewal: if the membership is still active,
 * the new period begins the day after the current expiry; otherwise it begins
 * on the payment date.
 *
 * NOTE: §14 open question — late payers may expect backdating. This is the
 * single switch to flip if the pilot gym wants that behaviour.
 */
export function computePeriod(args: {
  paidOn: IsoDate;
  currentExpiry: IsoDate | null;
  plan: Pick<MembershipPlan, 'durationUnit' | 'durationCount'>;
}): { periodStart: IsoDate; periodEnd: IsoDate } {
  const paid = parseDate(args.paidOn);
  const stillActive = args.currentExpiry && daysBetween(today(), parseDate(args.currentExpiry)) >= 0;
  const start = stillActive ? addDays(parseDate(args.currentExpiry!), 1) : paid;
  const { durationUnit, durationCount } = args.plan;
  let end: Date;
  if (durationUnit === 'MONTH') {
    // Anchor on the renewal day so it lands on the same calendar day.
    const anchor = stillActive ? parseDate(args.currentExpiry!) : paid;
    end = addMonths(anchor, durationCount);
  } else {
    end = addDays(start, durationCount - 1);
  }
  return { periodStart: toIso(start), periodEnd: toIso(end) };
}
